import { TextField, Box, ButtonGroup, IconButton, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { ExpandLess, ExpandMore, NavigateBefore, NavigateNext, AddRounded, RemoveRounded, Edit } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { updateGrid } from '../gridUtils.js';
import { useChartData } from './ChartDataProvider.js';

const MAX_GRID_SIZE = 1024;

const GridPanel = () => {
  const { data, knitdata, rows, cols, actions } = useChartData();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [tempRows, setTempRows] = useState(rows);
  const [tempCols, setTempCols] = useState(cols);

  // Sync tempRows and tempCols when actual rows/cols change
  useEffect(() => {
    setTempRows(rows);
    setTempCols(cols);
  }, [rows, cols]);

  function onUpdateGrid(loc, add) {
    // Prevent removing if it would result in 0 rows or columns
    if (!add) {
      if ((loc === 'top' || loc === 'bottom') && rows <= 1) {
        return; // Don't remove if only 1 row
      }
      if ((loc === 'left' || loc === 'right') && cols <= 1) {
        return; // Don't remove if only 1 column
      }
    }
    
    // Update both data and knitdata arrays with appropriate fill values
    let newdata = updateGrid(data, loc, add, 1, 0);  // Fill with 0 for color data
    let newknitdata = updateGrid(knitdata, loc, add, 1, 'k');  // Fill with 'k' for stitch data
    
    actions.updateData(newdata);
    actions.updateKnitdata(newknitdata);
  }

  function handleOpenEditDialog() {
    setTempRows(rows);
    setTempCols(cols);
    setEditDialogOpen(true);
  }

  function handleCloseEditDialog() {
    setEditDialogOpen(false);
  }

  function handleUpdateGrid() {
    // Convert empty strings to current values, then clamp to valid range
    const finalRows = tempRows === '' ? rows : tempRows;
    const finalCols = tempCols === '' ? cols : tempCols;
    const clampedRows = Math.max(1, Math.min(finalRows, MAX_GRID_SIZE));
    const clampedCols = Math.max(1, Math.min(finalCols, MAX_GRID_SIZE));
    
    let newdata = data;
    
    // Handle rows change first
    if (clampedRows !== rows) {
      const rowDiff = clampedRows - rows;
      newdata = updateGrid(newdata, 'bottom', rowDiff > 0, Math.abs(rowDiff));
    }
    
    // Then handle columns change
    if (clampedCols !== cols) {
    const colDiff = clampedCols - cols;
    newdata = updateGrid(newdata, 'right', colDiff > 0, Math.abs(colDiff));
  }
  
  // Update both data and knitdata arrays with all changes applied
  if (clampedRows !== rows || clampedCols !== cols) {
    // Apply the same grid changes to knitdata array
    let newknitdata = [...knitdata];
    
    // Update rows in knitdata
    if (clampedRows !== rows) {
      const rowDiff = clampedRows - rows;
      newknitdata = updateGrid(newknitdata, 'bottom', rowDiff > 0, Math.abs(rowDiff), 'k');
    }
    
    // Update columns in knitdata
    if (clampedCols !== cols) {
      const colDiff = clampedCols - cols;
      newknitdata = updateGrid(newknitdata, 'right', colDiff > 0, Math.abs(colDiff), 'k');
    }
    
    actions.updateData(newdata);
    actions.updateKnitdata(newknitdata);
  }    setEditDialogOpen(false);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      {/* Current Size Display */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <Typography variant="caption" sx={{ lineHeight: 1 }}>
            Cols × Rows
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {cols} × {rows}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleOpenEditDialog}>
          <Edit fontSize="small" />
        </IconButton>
      </Box>
      
      {/* Add/Remove Controls */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Top Controls */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ExpandLess fontSize="small" /> Top
          </Typography>
          <ButtonGroup size="small">
            <IconButton onClick={() => onUpdateGrid('top', true)} title="Add row at top">
              <AddRounded />
            </IconButton>
            <IconButton onClick={() => onUpdateGrid('top', false)} title="Remove row from top">
              <RemoveRounded />
            </IconButton>
          </ButtonGroup>
        </Box>
        
        {/* Left and Right Controls */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <NavigateBefore fontSize="small" /> Left
            </Typography>
            <ButtonGroup orientation="vertical" size="small">
              <IconButton onClick={() => onUpdateGrid('left', true)} title="Add column at left">
                <AddRounded />
              </IconButton>
              <IconButton onClick={() => onUpdateGrid('left', false)} title="Remove column from left">
                <RemoveRounded />
              </IconButton>
            </ButtonGroup>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Right <NavigateNext fontSize="small" />
            </Typography>
            <ButtonGroup orientation="vertical" size="small">
              <IconButton onClick={() => onUpdateGrid('right', true)} title="Add column at right">
                <AddRounded />
              </IconButton>
              <IconButton onClick={() => onUpdateGrid('right', false)} title="Remove column from right">
                <RemoveRounded />
              </IconButton>
            </ButtonGroup>
          </Box>
        </Box>
        
        {/* Bottom Controls */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Bottom <ExpandMore fontSize="small" />
          </Typography>
          <ButtonGroup size="small">
            <IconButton onClick={() => onUpdateGrid('bottom', true)} title="Add row at bottom">
              <AddRounded />
            </IconButton>
            <IconButton onClick={() => onUpdateGrid('bottom', false)} title="Remove row from bottom">
              <RemoveRounded />
            </IconButton>
          </ButtonGroup>
        </Box>
      </Box>
      
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog}>
        <DialogTitle>Edit Grid Size</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', gap: 2, minHeight: 80 }}>
          <TextField
            label="Columns"
            type="number"
            value={tempCols}
            slotProps={{ htmlInput: { min: 1, max: MAX_GRID_SIZE } }}
            onChange={(e) => {
              const inputValue = e.target.value;
              if (inputValue === '') {
                setTempCols('');
                return;
              }
              const value = Number(inputValue);
              if (isNaN(value)) return;
              const clampedValue = Math.max(1, Math.min(value, MAX_GRID_SIZE));
              setTempCols(clampedValue);
            }}
            size="small"
          />
          <TextField
            label="Rows"
            type="number"
            value={tempRows}
            slotProps={{ htmlInput: { min: 1, max: MAX_GRID_SIZE } }}
            onChange={(e) => {
              const inputValue = e.target.value;
              if (inputValue === '') {
                setTempRows('');
                return;
              }
              const value = Number(inputValue);
              if (isNaN(value)) return;
              const clampedValue = Math.max(1, Math.min(value, MAX_GRID_SIZE));
              setTempRows(clampedValue);
            }}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleUpdateGrid} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GridPanel;
